import React, { createContext, useReducer, useContext, useEffect } from 'react';
import { mockZones } from '../data/mockData';

// Initial state for the zone context
const initialState = {
  zones: [],
  currentZone: null,
  loading: false,
  error: null,
  notification: null
};

// Action types
const ACTION_TYPES = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_ZONES: 'SET_ZONES',
  SET_CURRENT_ZONE: 'SET_CURRENT_ZONE',
  ADD_ZONE: 'ADD_ZONE',
  UPDATE_ZONE: 'UPDATE_ZONE',
  DELETE_ZONE: 'DELETE_ZONE',
  SET_NOTIFICATION: 'SET_NOTIFICATION',
  CLEAR_NOTIFICATION: 'CLEAR_NOTIFICATION'
};

// Reducer function
const zoneReducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.SET_LOADING:
      return {
        ...state,
        loading: action.payload
      };
    
    case ACTION_TYPES.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false
      };
    
    case ACTION_TYPES.SET_ZONES:
      return {
        ...state,
        zones: action.payload,
        loading: false
      };
    
    case ACTION_TYPES.SET_CURRENT_ZONE:
      return {
        ...state,
        currentZone: action.payload
      };
    
    case ACTION_TYPES.ADD_ZONE:
      return {
        ...state,
        zones: [...state.zones, action.payload],
        loading: false
      };
    
    case ACTION_TYPES.UPDATE_ZONE:
      return {
        ...state,
        zones: state.zones.map(zone => 
          zone.id === action.payload.id ? { ...zone, ...action.payload } : zone
        ),
        currentZone: state.currentZone?.id === action.payload.id 
          ? { ...state.currentZone, ...action.payload } 
          : state.currentZone,
        loading: false
      };
    
    case ACTION_TYPES.DELETE_ZONE:
      return {
        ...state,
        zones: state.zones.filter(zone => zone.id !== action.payload),
        currentZone: state.currentZone?.id === action.payload ? null : state.currentZone,
        loading: false
      };
    
    case ACTION_TYPES.SET_NOTIFICATION:
      return {
        ...state,
        notification: action.payload
      };
    
    case ACTION_TYPES.CLEAR_NOTIFICATION:
      return {
        ...state,
        notification: null
      };
    
    default:
      return state;
  }
};

// Create context
export const ZoneContext = createContext();

/**
 * ZoneContextProvider component for managing zone state
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 */
export const ZoneContextProvider = ({ children }) => {
  const [state, dispatch] = useReducer(zoneReducer, initialState);
  
  // Load zones on mount
  useEffect(() => {
    loadZones();
  }, []);
  
  // Load zones from API or mock data
  const loadZones = async () => {
    dispatch({ type: ACTION_TYPES.SET_LOADING, payload: true });
    
    try {
      // In a real app, we would fetch from API
      // const response = await api.get('/zones');
      // const zones = response.data;
      
      // For now, use mock data
      setTimeout(() => {
        dispatch({ type: ACTION_TYPES.SET_ZONES, payload: mockZones });
      }, 500);
    } catch (error) {
      console.error('Error loading zones:', error);
      dispatch({ 
        type: ACTION_TYPES.SET_ERROR, 
        payload: 'Failed to load zones. Please try again.' 
      });
    }
  };
  
  // Get a single zone by ID
  const getZoneById = async (zoneId) => {
    dispatch({ type: ACTION_TYPES.SET_LOADING, payload: true });
    
    try {
      // Check if we already have the zone in state
      const existingZone = state.zones.find(zone => zone.id === zoneId);
      
      if (existingZone) {
        dispatch({ type: ACTION_TYPES.SET_CURRENT_ZONE, payload: existingZone });
        dispatch({ type: ACTION_TYPES.SET_LOADING, payload: false });
        return;
      }
      
      // In a real app, we would fetch from API
      // const response = await api.get(`/zones/${zoneId}`);
      // const zone = response.data;
      
      // For now, use mock data
      const zone = mockZones.find(zone => zone.id === zoneId);
      
      if (zone) {
        dispatch({ type: ACTION_TYPES.SET_CURRENT_ZONE, payload: zone });
      } else {
        dispatch({ 
          type: ACTION_TYPES.SET_ERROR, 
          payload: `Zone with ID ${zoneId} not found.` 
        });
      }
    } catch (error) {
      console.error('Error getting zone:', error);
      dispatch({ 
        type: ACTION_TYPES.SET_ERROR, 
        payload: 'Failed to load zone details. Please try again.' 
      });
    }
  };
  
  // Add a new zone
  const addZone = async (zoneData) => {
    dispatch({ type: ACTION_TYPES.SET_LOADING, payload: true });
    
    try {
      // In a real app, we would post to API
      // const response = await api.post('/zones', zoneData);
      // const newZone = response.data;
      
      // For now, create a mock zone with ID
      const newZone = {
        ...zoneData,
        id: `zone-${Date.now()}`,
        createdAt: new Date().toISOString()
      };
      
      dispatch({ type: ACTION_TYPES.ADD_ZONE, payload: newZone });
      showNotification('success', 'Zone added successfully');
    } catch (error) {
      console.error('Error adding zone:', error);
      dispatch({ 
        type: ACTION_TYPES.SET_ERROR, 
        payload: 'Failed to add zone. Please try again.' 
      });
    }
  };
  
  // Update a zone
  const updateZone = async (zoneId, updates) => {
    dispatch({ type: ACTION_TYPES.SET_LOADING, payload: true });
    
    try {
      // In a real app, we would put to API
      // const response = await api.put(`/zones/${zoneId}`, updates);
      // const updatedZone = response.data;
      
      // For now, update the zone directly
      const updatedZone = { 
        id: zoneId, 
        ...updates, 
        updatedAt: new Date().toISOString() 
      };
      
      dispatch({ type: ACTION_TYPES.UPDATE_ZONE, payload: updatedZone });
      showNotification('success', 'Zone updated successfully');
    } catch (error) {
      console.error('Error updating zone:', error);
      dispatch({ 
        type: ACTION_TYPES.SET_ERROR, 
        payload: 'Failed to update zone. Please try again.' 
      });
    }
  };
  
  // Delete a zone
  const deleteZone = async (zoneId) => {
    dispatch({ type: ACTION_TYPES.SET_LOADING, payload: true });
    
    try {
      // In a real app, we would delete from API
      // await api.delete(`/zones/${zoneId}`);
      
      // For now, just remove from state
      dispatch({ type: ACTION_TYPES.DELETE_ZONE, payload: zoneId });
      showNotification('info', 'Zone deleted successfully');
    } catch (error) {
      console.error('Error deleting zone:', error);
      dispatch({ 
        type: ACTION_TYPES.SET_ERROR, 
        payload: 'Failed to delete zone. Please try again.' 
      });
    }
  };
  
  // Show notification
  const showNotification = (type, message, duration = 3000) => {
    dispatch({ 
      type: ACTION_TYPES.SET_NOTIFICATION, 
      payload: { type, message } 
    });
    
    setTimeout(() => {
      dispatch({ type: ACTION_TYPES.CLEAR_NOTIFICATION });
    }, duration);
  };
  
  // Clear error
  const clearError = () => {
    dispatch({ type: ACTION_TYPES.SET_ERROR, payload: null });
  };
  
  // Context value
  const contextValue = {
    state,
    loadZones,
    getZoneById,
    addZone,
    updateZone,
    deleteZone,
    showNotification,
    clearError
  };
  
  return (
    <ZoneContext.Provider value={contextValue}>
      {children}
    </ZoneContext.Provider>
  );
};

// Custom hook for using the zone context
export const useZone = () => {
  const context = useContext(ZoneContext);
  
  if (!context) {
    throw new Error('useZone must be used within a ZoneContextProvider');
  }
  
  return context;
};
